import { useState, useEffect } from 'react';
import { Link, useI18next } from 'gatsby-plugin-react-i18next';
import { memo } from 'react';
import Drawer from '../drawer';
import ChangeLanguage from '../changeLanguage';
import DrawerMobile from '../drawerMobile';
import ImageDisplay from '../imageDisplay';
import DropdownIcon from '../../images/dropdown.inline.svg';
import HamburgerIcon from '../../images/hamburger.inline.svg';
import CloseIcon from '../../images/close.inline.svg';
import useComponentVisible from '../../hooks/useComponentVisible';
import PropTypes from 'prop-types';

import { languagesMap, shortLanguagesMap } from '../../utils/helpers';

const _Header = ({ logo, links, cta, phone, faqs }) => {
	const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
	const { language, changeLanguage } = useI18next();
	const [currLang, setLang] = useState('Eng');
	const weglotLang =
		(typeof window !== 'undefined' && window?.localStorage.getItem('wglang')) || 'en';

	useEffect(() => {
		setLang(shortLanguagesMap[weglotLang]);
	});

	const filterCurrLang = (langs) => {
		const tmpObj = Object.assign({}, langs);
		delete tmpObj[weglotLang];
		return tmpObj;
	};

	const handleSwitch = (value) => {
		window?.Weglot?.switchTo(value);
		setTimeout(() => {
			setLang(shortLanguagesMap[value]);
			changeLanguage(value);
		});
	};

	const onShowDrawerClick = () => {
		setIsComponentVisible(!isComponentVisible);
	};

	const getMobileLinks = (options) => {
		const mobileOptions = [];
		options.map((option) => {
			if (option.links) {
				option.links.map((link) =>
					mobileOptions.push({ title: link.title, link: link.cta.link })
				);
			} else {
				mobileOptions.push({
					title: option.linkLabel,
					link: option.linkDestination,
				});
			}
		});
		return mobileOptions;
	};

	useEffect(() => {
		handleSwitch(language);
		return;
	}, [language]);

	return (
		<header ref={ref} role="banner" className="cc-header">
			<div className="cc-header__wrap">
				<section className="cc-header__left-section">
					<Link
						tabIndex="0"
						to="/"
						className="cc-header__logo btn btn--transparent"
					>
						<ImageDisplay
							data={logo?.gatsbyImageData}
							src={logo?.file?.url}
							alt={logo?.description}
						/>
					</Link>
					<div className="cc-header__breadcrumb">
						{links.map((link) =>
							link.links ? (
								<div className="cc-header__dropdown" key={link.linkTitle}>
									<button
										tabIndex="0"
										role="button"
										className="btn btn--transparent typography typography--body-small-bold"
										onClick={onShowDrawerClick}
										aria-haspopup="true"
										aria-expanded={isComponentVisible}
									>
										{link.linkTitle}
										<DropdownIcon
											aria-labelledby="DropdownIcon ExpandDropDown"
											role="img"
											className="cc-header__dropdown-link"
										/>
									</button>
									<Drawer
										title={link.linkTitle}
										options={link.links}
										show={isComponentVisible}
									/>
								</div>
							) : (
								<Link
									className="cc-header__link typography typography--body-small-bold"
									to={link.linkDestination}
									key={link.linkLabel}
								>
									{link.linkLabel}
								</Link>
							)
						)}
					</div>
				</section>
				<section className="cc-header__right-section">
					<ChangeLanguage
						className="cc-header__dropdown"
						currLang={currLang}
						languagesMap={filterCurrLang(languagesMap)}
						shortLanguagesMap={filterCurrLang(shortLanguagesMap)}
						handleSwitch={handleSwitch}
					/>
					<a
						href={cta.link}
						className="link link--no-underline"
						target="_blank"
						rel="noreferrer"
					>
						<div className="cc-header__book-button btn">{cta.title}</div>
					</a>
					<a
						className="cc-header__link"
						href={`tel:${phone?.cta?.link}`}
						tabIndex="0"
					>
						<button
							role="button"
							className="cc-header__phone-button btn btn--transparent"
						>
							<img alt={phone?.title} src={phone.icon.file.url} />
						</button>
					</a>
					<Link
						className="cc-header__link cc-header__faq-button"
						tabIndex="0"
						to={faqs.cta.link}
					>
						<img alt={faqs?.cta.title} src={faqs.icon.file.url} />
					</Link>
					<button
						tabIndex="0"
						role="button"
						className="cc-header__hamburger btn btn--transparent"
						onClick={onShowDrawerClick}
					>
						{!isComponentVisible ? (
							<HamburgerIcon
								aria-labelledby="HamburgerIcon ExpandHamburger"
								role="img"
							/>
						) : (
							<CloseIcon aria-labelledby="CloseIcon Collapse" role="img" />
						)}
					</button>
				</section>
				<DrawerMobile
					options={getMobileLinks(links)}
					cta={cta}
					phone={phone}
					faqs={faqs}
					show={isComponentVisible}
					currLang={currLang}
					languagesMap={filterCurrLang(languagesMap)}
					handleSwitch={handleSwitch}
				/>
			</div>
		</header>
	);
};

_Header.propTypes = {
	logo: PropTypes.shape({
		description: PropTypes.string.isRequired,
		file: PropTypes.object.isRequired,
		gatsbyImageData: PropTypes.object,
	}).isRequired,
	links: PropTypes.arrayOf(PropTypes.object),
	cta: PropTypes.shape({
		title: PropTypes.string.isRequired,
		link: PropTypes.string.isRequired,
	}).isRequired,
	phone: PropTypes.shape({
		cta: PropTypes.shape({
			title: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
		title: PropTypes.string.isRequired,
		icon: PropTypes.object.isRequired,
		description: PropTypes.shape({
			raw: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
	faqs: PropTypes.shape({
		cta: PropTypes.shape({
			title: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
		title: PropTypes.string.isRequired,
		icon: PropTypes.object.isRequired,
		description: PropTypes.shape({
			raw: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
};

_Header.defaultProps = {
	show: false,
};

const Header = memo(_Header);

export default Header;
